// Tiny typed fetch wrapper for the dashboard SPA. Auto-redirects to /login
// on 401, parses JSON, throws on non-2xx with the JSON body when available.

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)['content-type'] = 'application/json';
  }
  const res = await fetch(path, init);

  if (res.status === 401) {
    const here = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/login?redirect_after=${here}`;
    return new Promise<T>(() => {}); // never resolves; navigation in flight
  }

  if (res.status === 204) return undefined as T;

  const json = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(errorMessage(res.status, json)) as ApiError;
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json as T;
}

// Turn a non-2xx response into a human-facing message. A 429 (rate limited) is
// surfaced as friendly copy with a wait hint instead of the raw `rate_limited` /
// `too_many_attempts` token — this is what the AI-generation modal and every
// other caller display in their catch (P13).
function errorMessage(status: number, json: unknown): string {
  if (status === 429) {
    const retry =
      json && typeof json === 'object' && 'retryAfterS' in json
        ? Number((json as { retryAfterS: unknown }).retryAfterS)
        : NaN;
    const wait =
      Number.isFinite(retry) && retry > 0
        ? retry < 60
          ? `${Math.ceil(retry)} seconds`
          : `${Math.ceil(retry / 60)} minute${retry >= 120 ? 's' : ''}`
        : 'a moment';
    return `You're going a bit fast. Please wait ${wait} and try again.`;
  }
  return json && typeof json === 'object' && 'error' in json
    ? String((json as { error: unknown }).error)
    : `HTTP ${status}`;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
